/**
 * Pure helpers backing the global primary-score filter slider in
 * `LigandMod` and `GlobalFilterSlider`. All exports are pure functions
 * of their inputs — no React, no module-level state — so they're
 * straightforward to unit-test.
 */
import { BestPoseBindingInfo, poseExchemP } from 'BMapsModel/FragmentSearchResult';

/**
 * Slider step granularity. Shared between `GlobalFilterSlider` (which
 * uses it as the `<input type=range>` step) and `buildGlobalScoreFilter`
 * (which uses half a step as the tolerance for "Showing all"). Anchoring
 * both consumers to one constant keeps the slider readout and the
 * filter's permissive-end check in lock-step.
 */
export const FILTER_SLIDER_STEP = 0.5;

/** @typedef {import('BMapsModel').DecodedFragment} DecodedFragment */

/**
 * Build a value-extractor that returns the logical `primaryScoreField`
 * value for a raw `DecodedFragment`.
 *
 * For `enSolute` (and any other field that maps directly) this is a
 * plain property read; for `bindingScore` it applies the
 * `bindingFE / 1.36` conversion (see `BestPoseBindingInfo.getBindingScore`)
 * so the slider's range and the filter predicate operate in the same
 * units as the "Binding score" column the user sees — *not* the raw
 * `exchemPotential` underlying it.
 *
 * gen2 poses have no binding score (`poseExchemP` returns null), so for the
 * `bindingScore` field they yield null: excluded from the slider's computed bounds
 * (`computePrimaryScoreRange` skips non-finite) and passed through by the filter
 * predicate, exactly like any pose lacking a value in the active field.
 *
 * @param {'enSolute'|'bindingScore'} primaryScoreField
 * @returns {function(DecodedFragment): number | null}
 */
export function fragValueForField(primaryScoreField) {
    if (primaryScoreField === 'bindingScore') {
        return (frag) => BestPoseBindingInfo.getBindingScore(poseExchemP(frag));
    }
    return (frag) => frag[primaryScoreField];
}

/**
 * Compute the [min, max] range of the primary-score property over every
 * pose in every suggestion. Used to set the bounds of the primary-score
 * filter slider. Poses whose value is not a finite number are skipped
 * (they are also passed through unfiltered by the slider, so the range
 * reflects only filterable poses).
 *
 * Returns `{ min: 0, max: 0 }` for an empty input or if no pose has a
 * numeric value, so the slider remains valid even before results arrive.
 *
 * @param {{ frags: DecodedFragment[] }[]} suggestions
 * @param {'enSolute'|'bindingScore'} primaryScoreField
 * @returns {{ min: number, max: number }}
 */
export function computePrimaryScoreRange(suggestions, primaryScoreField) {
    const getValue = fragValueForField(primaryScoreField);
    let min = Infinity;
    let max = -Infinity;
    for (const sugg of suggestions) {
        for (const frag of sugg.frags) {
            const v = getValue(frag);
            if (Number.isFinite(v)) {
                if (v < min) min = v;
                if (v > max) max = v;
            }
        }
    }
    return (!Number.isFinite(min) || !Number.isFinite(max))
        ? { min: 0, max: 0 }
        : { min, max };
}

/**
 * The "permissive" end of the slider — the value at which the filter
 * admits every pose in the current data range.
 *
 * Both `enSolute` and `bindingScore` are "more-negative is better" axes
 * (mirroring `AnnotatedSearchResult.isBetterThan`'s `higherIsBetter=false`
 * default and `sortFrags`' ascending sort on bindingScore), so the
 * predicate is uniformly `v <= threshold` and the permissive end is the
 * range maximum.
 *
 * The raw `range.max` is *snapped up* (toward zero) to the nearest
 * `FILTER_SLIDER_STEP` boundary so the slider's step grid lands on clean
 * multiples of the step — without snapping, the grid inherits whatever
 * fractional part the data max happens to have and readouts come out
 * like `-30.4` instead of `-30.5`. Snapping *up* preserves the
 * permissive-end invariant: every frag's value is `<= range.max`, and
 * `range.max <= snapped_max`, so every frag still passes at the
 * snapped permissive end.
 *
 * @param {{ min: number, max: number }} range
 * @returns {number}
 */
export function permissiveEnd(range) {
    return Math.ceil(range.max / FILTER_SLIDER_STEP) * FILTER_SLIDER_STEP;
}

/**
 * Build the per-pose predicate used by the primary-score filter.
 *
 * Both `enSolute` and `bindingScore` are more-negative-is-better, so the
 * predicate is uniformly `v <= threshold`. The value is extracted via
 * `fragValueForField`, which applies the `bindingScore = exchemPotential
 * / 1.36` conversion when the active field is `bindingScore` — keeping
 * the threshold in the same units the user sees in the table.
 *
 * Poses whose value is not a finite number are passed through
 * unconditionally so the slider never hides poses that lack a score in
 * the active field. This pairs with `computePrimaryScoreRange`, which
 * excludes the same poses from the slider bounds.
 *
 * @param {number} threshold
 * @param {'enSolute'|'bindingScore'} primaryScoreField
 * @returns {function(DecodedFragment): boolean}
 */
export function passesPrimaryScoreFilter(threshold, primaryScoreField) {
    const getValue = fragValueForField(primaryScoreField);
    return (frag) => {
        const v = getValue(frag);
        return !Number.isFinite(v) || v <= threshold;
    };
}

/**
 * Build the global-filter `PosePropertyFilter` for the active scoring
 * field at the given threshold, or return `null` when no filtering is
 * needed (threshold absent, or sitting at the permissive end of the data
 * range). The returned object is passed to `recalculateDisplayPoses` as
 * the separate `globalFilter` argument — it intentionally never enters
 * `state.allDisplayFilters`.
 *
 * Takes `suggestions` rather than a precomputed range so the range
 * computation is skipped entirely when the threshold is absent (the
 * common case for users who never touch the slider).
 *
 * @param {number|undefined} threshold
 * @param {'enSolute'|'bindingScore'} primaryScoreField
 * @param {{ frags: DecodedFragment[] }[]} suggestions
 * @returns {{ value: number, filter: function(DecodedFragment): boolean } | null}
 */
export function buildGlobalScoreFilter(threshold, primaryScoreField, suggestions) {
    if (threshold === undefined) return null;
    // Tolerance is half a step — HTML range inputs emit strings and can
    // drift by a tiny float ε around step boundaries, so a strict equality
    // check would leave the filter armed even when the user has dragged
    // back to the permissive end. Half a step also guarantees that the
    // first step inside the range (permissive − step) is OUTSIDE the
    // null window, so the filter actually engages on any real movement.
    const range = computePrimaryScoreRange(suggestions, primaryScoreField);
    if (threshold >= permissiveEnd(range) - FILTER_SLIDER_STEP / 2) return null;
    return {
        value: threshold,
        filter: passesPrimaryScoreFilter(threshold, primaryScoreField),
    };
}
